import isEqual from 'lodash/isEqual';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ChromeExtensionMessages,
  DugoutLocations,
  SupportedDomains
} from '@/constants/chromeExtension';
import ENVIRONMENTS from '@/constants/environments';
import HOSTS from '@/constants/hosts';
import AppContext from '@/contexts/appContext';
import type { Action } from '@/types/chromeExtension';
import {
  hasPermission,
  PEOPLE_VIEW_ENCOUNTERS,
  PEOPLE_VIEW_REWARD_CARDS
} from '@/utils/userPermission';

import { useItemByKustomerEventId } from './useItem';
import useNavigateToPage from './useNavigateToPage';
import usePerson from './usePerson';

const useChromeExtensionAction = () => {
  // Ref is used to avoid unnecessary refreshes
  const actionRef = useRef<Action | null>(null);
  // Current action must be saved to avoid unnecessary refreshes in the future
  const [currentAction, setCurrentAction] = useState<Action | null>(null);
  const [reRunnedAction, setReRunnedAction] = useState(false);
  const [isLoading] = useState(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);

  const { permissions, host, setHost } = useContext(AppContext);
  const enableEncounters = useMemo((): boolean => {
    const requiredPermission = PEOPLE_VIEW_ENCOUNTERS;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);
  const enableRewardCards = useMemo((): boolean => {
    const requiredPermission = PEOPLE_VIEW_REWARD_CARDS;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const {
    navigateToActivityLog,
    navigateToChromeExtensionRoot,
    navigateToEncounters,
    navigateToEncounterWithoutPerson,
    navigateToEncounterBillingWithoutPerson,
    navigateToProfile,
    navigateToProfileWithId,
    navigateToCms1500,
    navigateToCms1500ByCandidEncounterId,
    navigateToRewardCards,
    navigateToTask,
    navigateToTransactions,
    navigateToScheduling
  } = useNavigateToPage();
  const {
    usePersonQuery: { refetch: getPersonData }
  } = usePerson({
    id: currentAction?.patientId ?? currentAction?.id,
    platform: currentAction?.platform,
    enabled: false
  });
  const { refetch: getTaskData } = useItemByKustomerEventId({
    kustomerEventId: currentAction?.taskId,
    enabled: false
  });
  const navigate = useNavigate();

  const setAction = useCallback(
    (newAction: Action | null) => {
      if (!isEqual(actionRef.current, newAction)) {
        actionRef.current = newAction;
        setCurrentAction(newAction);
        if (!newAction) navigateToChromeExtensionRoot();
      }
    },
    [navigateToChromeExtensionRoot]
  );

  const reRunAction = useCallback(() => setReRunnedAction((current) => !current), []);

  /**
   * Determines the appropriate host based on the provided origin.
   *
   * This function uses the origin url to determine which environment
   *
   * @param origin - The origin url to evaluate.
   * @returns The host where data should be fetched for the given origin.
   */
  const hostForOrigin = useCallback((origin: string) => {
    if (origin.includes('localhost')) {
      return HOSTS[ENVIRONMENTS.LOCAL];
    } else if (origin.includes('staging') || origin.includes('sandbox')) {
      return HOSTS[ENVIRONMENTS.STAGING];
    } else {
      return HOSTS[ENVIRONMENTS.PRODUCTION];
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (currentAction && currentAction.type === ChromeExtensionMessages.NEW_ACTION) {
        const newHost = hostForOrigin(currentAction.origin);
        if (newHost !== host) {
          setHost(newHost);
        }

        // Extension messages handlers
        switch (currentAction.location) {
          case DugoutLocations.CMS1500:
            if (currentAction.id) {
              // Healthie hands us a CMS1500 id directly; Candid only ever
              // exposes its own encounter UUID in the claim URL, so each
              // platform needs its own lookup route.
              currentAction.platform === SupportedDomains.CANDID
                ? navigateToCms1500ByCandidEncounterId(currentAction.id)
                : navigateToCms1500(currentAction.id);
            }
            break;
          case DugoutLocations.PROFILE:
            if (currentAction?.id && currentAction?.platform === SupportedDomains.HOMEBASE)
              navigateToProfileWithId(currentAction.id);
            else if (currentAction?.id || currentAction?.patientId) {
              setIsBackgroundLoading(true);
              const { data } = await getPersonData();
              setIsBackgroundLoading(false);
              if (data?.data && !data.isError) {
                navigateToProfile(data.data);
              } else {
                setAction(null);
              }
            }
            break;
          case DugoutLocations.TASK:
            if (currentAction.taskId) {
              setIsBackgroundLoading(true);
              const { data, isError } = await getTaskData();
              setIsBackgroundLoading(false);
              if (data?.data?.id && !isError) navigateToTask(data.data);
              else if (currentAction?.id || currentAction?.patientId) {
                if (currentAction?.id && currentAction?.platform === SupportedDomains.HOMEBASE)
                  navigateToProfileWithId(currentAction.id);
                else {
                  setIsBackgroundLoading(true);
                  const { data } = await getPersonData();
                  setIsBackgroundLoading(false);
                  if (data?.data && !data.isError) {
                    navigateToProfile(data.data);
                  } else {
                    setAction(null);
                  }
                }
              } else {
                setAction(null);
              }
            }
            break;
          case DugoutLocations.ENCOUNTERS: {
            if (currentAction.id && currentAction.encounterType) {
              const { encounterType, id } = currentAction;
              if (enableEncounters)
                navigateToEncounters({
                  encounterType: encounterType,
                  encounterHealthieId: id
                });
            }
            break;
          }
          case DugoutLocations.ENCOUNTER: {
            if (currentAction.encounterId && currentAction.encounterType) {
              const { encounterType, encounterId } = currentAction;
              if (enableEncounters) navigateToEncounterWithoutPerson(encounterType, encounterId);
            }
            break;
          }

          case DugoutLocations.ENCOUNTER_BILLING: {
            if (currentAction.encounterId && currentAction.encounterType) {
              const { encounterType, encounterId } = currentAction;
              if (enableEncounters)
                navigateToEncounterBillingWithoutPerson(encounterType, encounterId);
            }
            break;
          }
          case DugoutLocations.REWARD_CARDS:
          case DugoutLocations.TRANSACTIONS: {
            if (currentAction.id) {
              if (enableRewardCards) {
                currentAction.location === DugoutLocations.REWARD_CARDS
                  ? navigateToRewardCards(currentAction.id)
                  : navigateToTransactions(currentAction.id);
              } else navigateToProfileWithId(currentAction.id);
            }
            break;
          }
          case DugoutLocations.ACTIVITY_LOG:
            if (currentAction.formAnswerGroupId) {
              navigateToActivityLog(currentAction.formAnswerGroupId);
            }
            break;
          case DugoutLocations.SCHEDULING:
            if (currentAction.id) {
              setIsBackgroundLoading(true);
              const { data } = await getPersonData();
              setIsBackgroundLoading(false);
              if (data?.data && !data.isError) {
                navigateToScheduling(data.data, currentAction.providerId || '');
              } else {
                setAction(null);
              }
            }
            break;
          case DugoutLocations.RAW_PATH:
            if (currentAction.path) {
              navigate(currentAction.path);
            }
            break;
          default:
            setAction(null);
            break;
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentAction,
    enableEncounters,
    enableRewardCards,
    getPersonData,
    getTaskData,
    navigateToActivityLog,
    navigateToCms1500,
    navigateToCms1500ByCandidEncounterId,
    navigateToEncounters,
    navigateToEncounterWithoutPerson,
    navigateToEncounterBillingWithoutPerson,
    navigateToProfile,
    navigateToProfileWithId,
    navigateToRewardCards,
    navigateToTask,
    navigateToTransactions,
    reRunnedAction,
    setAction
  ]);

  return { currentAction, isLoading, isBackgroundLoading, reRunAction, setAction };
};

export default useChromeExtensionAction;
